import React, { useState, useMemo, useEffect, useRef, forwardRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import './InboundDetails.css';
import Header from './Header';
import SubNav from './SubNav';

// Custom input component for DatePicker with calendar icon
const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="input-group" onClick={onClick}>
    <input
      type="text"
      className="form-control date-input"
      value={value}
      placeholder={placeholder}
      readOnly
      ref={ref}
    />
    <span className="input-group-addon" style={{ cursor: 'pointer' }}>
      <img src="https://img1a.coupangcdn.com/image/coupang/retail/sc/scm/calendar.png" alt="calendar" />
    </span>
  </div>
));

function InboundDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    year: '2026',
    month: '4',
    startDate: '2026-04-01',
    endDate: '2026-04-30',
    requestSeq: '',
    vendorPaymentInfoSeq: ''
  });

  // Committed filters - only update when 검색 is clicked
  const [committedFilters, setCommittedFilters] = useState({
    year: '2026',
    month: '4',
    startDate: '2026-04-01',
    endDate: '2026-04-30'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [isLogisticsExpanded, setIsLogisticsExpanded] = useState(true);
  const [searchPerformed, setSearchPerformed] = useState(true);
  const [showStartMonthPicker, setShowStartMonthPicker] = useState(false);
  const [showEndMonthPicker, setShowEndMonthPicker] = useState(false);

  // Determine active menu based on current route
  const getActiveMenu = () => {
    if (location.pathname.startsWith('/scm/')) {
      return 'logistics';
    }
    return 'home';
  };

  const activeMenu = getActiveMenu();

  // Monthly data by account
  const monthlyData = {
    'elonmusk': {
      '2025-01': { totalUnitPrice: 241820400, totalOrderPrice: 219836727, totalVatPrice: 21983673, totalCount: 4312 },
      '2025-02': { totalUnitPrice: 108870000, totalOrderPrice: 98972727, totalVatPrice: 9897273, totalCount: 3421 },
      '2025-03': { totalUnitPrice: 234507900, totalOrderPrice: 213189000, totalVatPrice: 21318900, totalCount: 4108 },
      '2025-04': { totalUnitPrice: 276049000, totalOrderPrice: 250953636, totalVatPrice: 25095364, totalCount: 4012 },
      '2025-05': { totalUnitPrice: 212843900, totalOrderPrice: 193494455, totalVatPrice: 19349445, totalCount: 3802 },
      '2025-06': { totalUnitPrice: 238449300, totalOrderPrice: 216772091, totalVatPrice: 21677209, totalCount: 4188 },
      '2025-07': { totalUnitPrice: 248129384, totalOrderPrice: 225572167, totalVatPrice: 22557217, totalCount: 4011 },
      '2025-08': { totalUnitPrice: 272184950, totalOrderPrice: 247440864, totalVatPrice: 24744086, totalCount: 4092 },
      '2025-09': { totalUnitPrice: 302184928, totalOrderPrice: 274713571, totalVatPrice: 27471357, totalCount: 4244 },
      '2025-10': { totalUnitPrice: 296843700, totalOrderPrice: 269857000, totalVatPrice: 26986700, totalCount: 4302 },
      '2025-11': { totalUnitPrice: 204381400, totalOrderPrice: 185801273, totalVatPrice: 18580127, totalCount: 3711 },
      '2025-12': { totalUnitPrice: 186432300, totalOrderPrice: 169484818, totalVatPrice: 16947482, totalCount: 3612 },
      '2026-01': { totalUnitPrice: 195200000, totalOrderPrice: 177454545, totalVatPrice: 17745455, totalCount: 3650 },
      '2026-02': { totalUnitPrice: 201500000, totalOrderPrice: 183181818, totalVatPrice: 18318182, totalCount: 3700 },
      '2026-03': { totalUnitPrice: 215600000, totalOrderPrice: 196000000, totalVatPrice: 19600000, totalCount: 3800 },
      '2026-04': { totalUnitPrice: 102385100, totalOrderPrice: 93077363, totalVatPrice: 9307737, totalCount: 3744 }
    },
    'likezone': {
      '2025-01': { totalUnitPrice: 218304600, totalOrderPrice: 198458727, totalVatPrice: 19845873, totalCount: 4108 },
      '2025-02': { totalUnitPrice: 178623900, totalOrderPrice: 162385364, totalVatPrice: 16238536, totalCount: 4112 },
      '2025-03': { totalUnitPrice: 295184730, totalOrderPrice: 268349755, totalVatPrice: 26834975, totalCount: 4566 },
      '2025-04': { totalUnitPrice: 267543910, totalOrderPrice: 243221736, totalVatPrice: 24322174, totalCount: 4188 },
      '2025-05': { totalUnitPrice: 198375600, totalOrderPrice: 180341455, totalVatPrice: 18034145, totalCount: 3944 },
      '2025-06': { totalUnitPrice: 221538700, totalOrderPrice: 201398818, totalVatPrice: 20139882, totalCount: 3954 },
      '2025-07': { totalUnitPrice: 257623900, totalOrderPrice: 234203545, totalVatPrice: 23420355, totalCount: 4243 },
      '2025-08': { totalUnitPrice: 224398215, totalOrderPrice: 203998377, totalVatPrice: 20399838, totalCount: 4011 },
      '2025-09': { totalUnitPrice: 311284910, totalOrderPrice: 282077191, totalVatPrice: 29207719, totalCount: 4118 },
      '2025-10': { totalUnitPrice: 276518900, totalOrderPrice: 251381727, totalVatPrice: 25137173, totalCount: 4066 },
      '2025-11': { totalUnitPrice: 216497800, totalOrderPrice: 196816182, totalVatPrice: 19681618, totalCount: 3844 },
      '2025-12': { totalUnitPrice: 172843600, totalOrderPrice: 157130545, totalVatPrice: 15713055, totalCount: 3544 },
      '2026-01': { totalUnitPrice: 180200000, totalOrderPrice: 163818182, totalVatPrice: 16381818, totalCount: 3500 },
      '2026-02': { totalUnitPrice: 188400000, totalOrderPrice: 171272727, totalVatPrice: 17127273, totalCount: 3550 },
      '2026-03': { totalUnitPrice: 192000000, totalOrderPrice: 174545455, totalVatPrice: 17454545, totalCount: 3600 },
      '2026-04': { totalUnitPrice: 97845000, totalOrderPrice: 88950000, totalVatPrice: 8895000, totalCount: 3580 }
    },
    'traderb': {
      '2025-01': { totalUnitPrice: 232917300, totalOrderPrice: 211742091, totalVatPrice: 21175209, totalCount: 4056 },
      '2025-02': { totalUnitPrice: 96228400, totalOrderPrice: 87480364, totalVatPrice: 8748036, totalCount: 3709 },
      '2025-03': { totalUnitPrice: 312986400, totalOrderPrice: 284533091, totalVatPrice: 28453309, totalCount: 4413 },
      '2025-04': { totalUnitPrice: 229781600, totalOrderPrice: 208892364, totalVatPrice: 20889236, totalCount: 4321 },
      '2025-05': { totalUnitPrice: 219483700, totalOrderPrice: 199530636, totalVatPrice: 19953064, totalCount: 4016 },
      '2025-06': { totalUnitPrice: 219774600, totalOrderPrice: 199795091, totalVatPrice: 19979509, totalCount: 4033 },
      '2025-07': { totalUnitPrice: 241823140, totalOrderPrice: 219839218, totalVatPrice: 21983922, totalCount: 4106 },
      '2025-08': { totalUnitPrice: 251729884, totalOrderPrice: 228845349, totalVatPrice: 22884535, totalCount: 4396 },
      '2025-09': { totalUnitPrice: 287593746, totalOrderPrice: 261448860, totalVatPrice: 26144886, totalCount: 4517 },
      '2025-10': { totalUnitPrice: 287904100, totalOrderPrice: 261730091, totalVatPrice: 26174009, totalCount: 4014 },
      '2025-11': { totalUnitPrice: 199874200, totalOrderPrice: 181703818, totalVatPrice: 18170382, totalCount: 3966 },
      '2025-12': { totalUnitPrice: 181395400, totalOrderPrice: 164905818, totalVatPrice: 16489582, totalCount: 3894 },
      '2026-01': { totalUnitPrice: 190500000, totalOrderPrice: 173181818, totalVatPrice: 17318182, totalCount: 3900 },
      '2026-02': { totalUnitPrice: 198700000, totalOrderPrice: 180636364, totalVatPrice: 18063636, totalCount: 3950 },
      '2026-03': { totalUnitPrice: 205300000, totalOrderPrice: 186636364, totalVatPrice: 18663636, totalCount: 4000 },
      '2026-04': { totalUnitPrice: 111320000, totalOrderPrice: 101200000, totalVatPrice: 10120000, totalCount: 4050 }
    },
    'betweeninc': {
      '2025-01': { totalUnitPrice: 225393900, totalOrderPrice: 204904455, totalVatPrice: 20489445, totalCount: 4266 },
      '2025-02': { totalUnitPrice: 102385100, totalOrderPrice: 93077363, totalVatPrice: 9307737, totalCount: 3744 },
      '2025-03': { totalUnitPrice: 408237500, totalOrderPrice: 371125000, totalVatPrice: 37112500, totalCount: 4755 },
      '2025-04': { totalUnitPrice: 218473200, totalOrderPrice: 198612000, totalVatPrice: 19861200, totalCount: 3797 },
      '2025-05': { totalUnitPrice: 193211300, totalOrderPrice: 175646636, totalVatPrice: 17564664, totalCount: 3842 },
      '2025-06': { totalUnitPrice: 232886300, totalOrderPrice: 211714818, totalVatPrice: 21171482, totalCount: 4058 },
      '2025-07': { totalUnitPrice: 264689140, totalOrderPrice: 240626491, totalVatPrice: 24062649, totalCount: 4227 },
      '2025-08': { totalUnitPrice: 261483762, totalOrderPrice: 237712511, totalVatPrice: 23771251, totalCount: 4445 },
      '2025-09': { totalUnitPrice: 202864888, totalOrderPrice: 184331716, totalVatPrice: 18533172, totalCount: 4432 },
      '2025-10': { totalUnitPrice: 257205600, totalOrderPrice: 233823273, totalVatPrice: 23382327, totalCount: 4206 },
      '2025-11': { totalUnitPrice: 192194200, totalOrderPrice: 174631091, totalVatPrice: 17563109, totalCount: 3921 },
      '2025-12': { totalUnitPrice: 177693600, totalOrderPrice: 161538818, totalVatPrice: 16154782, totalCount: 3837 },
      '2026-01': { totalUnitPrice: 185400000, totalOrderPrice: 168545455, totalVatPrice: 16854545, totalCount: 3850 },
      '2026-02': { totalUnitPrice: 191200000, totalOrderPrice: 173818182, totalVatPrice: 17381818, totalCount: 3880 },
      '2026-03': { totalUnitPrice: 198500000, totalOrderPrice: 180454545, totalVatPrice: 18045455, totalCount: 3920 },
      '2026-04': { totalUnitPrice: 92730000, totalOrderPrice: 84300000, totalVatPrice: 8430000, totalCount: 3420 }
    }
  };

  // Calculate summary based on date range
  const summaryData = useMemo(() => {
    const username = user?.username || 'elonmusk';
    const userData = monthlyData[username];
    
    if (!userData) {
      return { totalUnitPrice: '0', totalOrderPrice: '0', totalVatPrice: '0', totalCount: '0' };
    }

    const startDate = new Date(committedFilters.startDate);
    const endDate = new Date(committedFilters.endDate);
    
    let totalUnitPrice = 0;
    let totalOrderPrice = 0;
    let totalVatPrice = 0;
    let totalCount = 0;

    // Iterate through each month in the date range
    for (let date = new Date(startDate); date <= endDate; date.setMonth(date.getMonth() + 1)) {
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthData = userData[monthKey];
      
      if (monthData) {
        totalUnitPrice += monthData.totalUnitPrice;
        totalOrderPrice += monthData.totalOrderPrice;
        totalVatPrice += monthData.totalVatPrice;
        totalCount += monthData.totalCount;
      }
    }

    return {
      totalUnitPrice: totalUnitPrice.toLocaleString(),
      totalOrderPrice: totalOrderPrice.toLocaleString(),
      totalVatPrice: totalVatPrice.toLocaleString(),
      totalCount: totalCount.toLocaleString()
    };
  }, [user, committedFilters.startDate, committedFilters.endDate]);

  // Generate 100 realistic inbound items based on selected date range
  const generateInboundData = (startDate, endDate, username) => {
    const types = ['발주'];
    const warehouses = ['INC14', 'INC32', 'INC21', 'INC45', 'INC08', 'INC19'];
    const taxTypes = ['과세', '면세'];

    const productNamesByUser = {
      'elonmusk': [
        '파이네스트원 어메이즈핏 액티브 44.45mm 액정보호 TPU 스마트워치 케이스 블랙 1개',
        '파이네스트원 어메이즈핏 GTS 4 MINI 41.8mm 액정보호 TPU 스마트워치 케이스 블랙 1개',
        '파이네스트원 어메이즈핏 액티브 44.45mm 액정보호 TPU 스마트워치 케이스 실버 1개',
        '파이네스트원 가민 265/265S 믹스 컬러 스마트워치 스트랩 화이트 블랙22mm(265)',
        '파이네스트원 가민 포러너 165 패턴 실리콘 스마트위치 스트랩 포기 그레이 20mm',
        '스마트워치 충전 케이블 USB 자석 충전기',
        '갤럭시 워치 스트랩 실리콘 밴드',
        '애플워치 밴드 스테인리스 메탈',
        '무선충전기 고속 충전 패드 15W',
        '강화유리 액정보호필름 9H 프리미엄',
        '스마트폰 거치대 차량용 송풍구 타입',
        'USB-C 충전 케이블 고속충전 5A',
        '보조배터리 20000mAh 대용량 PD',
        '핸드폰 케이스 투명 실리콘 범퍼 케이스',
        '블루투스 이어폰 무선 TWS 노이즈캔슬링',
        '노트북 파우치 13.3인치 방수',
        '키보드 청소 키트 브러쉬 세트',
        '마우스패드 게이밍 대형 XXL',
        '태블릿 거치대 접이식 각도조절',
        'USB 허브 7포트 고속 데이터'
      ],
      'likezone': [
        '킹스로드 예쁜 코스튬 머리띠 크라운 미키',
        '킹스로드 파티 왕관 머리띠 골드 실버',
        '킹스로드 생일 파티 풍선 세트 20개',
        '킹스로드 LED 야광 머리띠 파티용',
        '킹스로드 코스프레 가발 긴머리 블랙',
        '킹스로드 파티 안경 포토부스 소품',
        '킹스로드 반짝이 나비 머리핀 세트',
        '킹스로드 할로윈 코스튬 마녀 모자',
        '킹스로드 크리스마스 루돌프 머리띠',
        '킹스로드 공주 왕관 티아라 실버',
        '킹스로드 파티 마스크 세트 10개입',
        '킹스로드 파티 가랜드 배너 세트',
        '킹스로드 포토존 풍선 아치 세트',
        '킹스로드 생일 케이크 토퍼 숫자',
        '킹스로드 파티 폭죽 컨페티 크래커',
        '킹스로드 반짝이 별 머리띠 골드',
        '킹스로드 무지개 풍선 세트 50개',
        '킹스로드 할로윈 거미줄 장식 세트',
        '킹스로드 파티 앞치마 요리사 코스튬',
        '킹스로드 생일 현수막 맞춤 제작'
      ],
      'traderb': [
        '픽앤홈 캔버스 토트백 에코백 대형',
        '픽앤홈 면 파우치 화장품 수납 소형',
        '픽앤홈 가죽 카드지갑 명함 케이스',
        '픽앤홈 패브릭 쇼퍼백 숄더백 베이지',
        '픽앤홈 방수 백팩 노트북 수납 15L',
        '픽앤홈 미니 크로스백 지퍼 포켓',
        '픽앤홈 천연가죽 장지갑 슬림 블랙',
        '픽앤홈 여행용 세면도구 파우치 방수',
        '픽앤홈 린넨 쿠션 커버 45x45 그레이',
        '픽앤홈 코튼 블랭킷 소파 덮개 아이보리',
        '픽앤홈 맥북 슬리브 파우치 13인치',
        '픽앤홈 캠핑 접이식 가방 트롤리',
        '픽앤홈 니트 숄더백 버킷 백 브라운',
        '픽앤홈 재활용 PET 에코백 그린',
        '픽앤홈 가죽 벨트 파우치 허리 전화기',
        '픽앤홈 코듀로이 토트백 빈티지 네이비',
        '픽앤홈 메시 짐백 스포츠 드로스트링',
        '픽앤홈 주얼리 파우치 벨벳 소형',
        '픽앤홈 폴리에스터 여행 더플백 60L',
        '픽앤홈 프린트 패브릭 클러치 파우치'
      ],
      'betweeninc': [
        '비투윈 프리미엄 유산균 30포 1개월분',
        '비투윈 종합비타민 미네랄 60정',
        '비투윈 오메가3 EPA DHA 90캡슐',
        '비투윈 콜라겐 펩타이드 분말 200g',
        '비투윈 루테인 지아잔틴 60캡슐',
        '비투윈 마그네슘 글리시네이트 120정',
        '비투윈 비타민C 1000mg 100정',
        '비투윈 아연 아세테이트 50mg 60정',
        '비투윈 비타민D3 2000IU 90정',
        '비투윈 밀크씨슬 실리마린 60캡슐',
        '비투윈 코엔자임 Q10 100mg 60캡슐',
        '비투윈 쏘팔메토 추출물 320mg 60캡슐',
        '비투윈 글루코사민 콘드로이틴 90정',
        '비투윈 알파리포산 600mg 60캡슐',
        '비투윈 포스파티딜세린 100mg 60정',
        '비투윈 철분 비스글리시네이트 25mg',
        '비투윈 프로폴리스 농축액 500mg 60정',
        '비투윈 엽산 400mcg 180정',
        '비투윈 베타글루칸 면역 60캡슐',
        '비투윈 히알루론산 저분자 60정'
      ]
    };

    const productNames = productNamesByUser[username] || productNamesByUser['elonmusk'];
    
    const data = [];
    const startNumber = 115200000;
    
    // Parse date range - clamp to start month
    const start = new Date(startDate);
    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    // Use only the start month for date generation
    const monthStart = new Date(startYear, startMonth, 1);
    const monthEnd = new Date(startYear, startMonth + 1, 0, 23, 59, 59);
    const dateRangeMs = monthEnd.getTime() - monthStart.getTime();

    // Deterministic pseudo-random based on seed
    const seededRand = (seed) => {
      let s = seed;
      return () => {
        s = (s * 1664525 + 1013904223) & 0xffffffff;
        return (s >>> 0) / 0xffffffff;
      };
    };

    // Fixed seed based on year+month so dates stay same for same month
    const baseSeed = startYear * 100 + startMonth;

    // Format helpers (defined early so they can be used for firstPage values too)
    const formatDateTime = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Shared date/warehouse for first page (applied after shuffle)
    const firstPageRand = seededRand(baseSeed * 31337);
    const firstPageDateOffset = Math.floor(firstPageRand() * dateRangeMs);
    const firstPageDate = new Date(monthStart.getTime() + firstPageDateOffset);
    firstPageDate.setHours(Math.floor(firstPageRand() * 24), Math.floor(firstPageRand() * 60), Math.floor(firstPageRand() * 60));
    const firstPagePaymentDate = new Date(firstPageDate);
    firstPagePaymentDate.setDate(firstPagePaymentDate.getDate() + 60);
    const firstPageWarehouse = warehouses[Math.floor(firstPageRand() * warehouses.length)];
    
    for (let i = 0; i < 100; i++) {
      const rand = seededRand(baseSeed + i * 7919);
      const quantity = Math.floor(rand() * 10) + 1;
      const unitPrice = (Math.floor(rand() * 15) + 3) * 1000;
      const supplyPrice = Math.floor(unitPrice / 1.1);
      const vatPrice = unitPrice - supplyPrice;

      // Deterministic date strictly within the start month
      const dateOffset = Math.floor(rand() * dateRangeMs);
      const randomDate = new Date(monthStart.getTime() + dateOffset);
      randomDate.setHours(Math.floor(rand() * 24), Math.floor(rand() * 60), Math.floor(rand() * 60));
      const warehouse = warehouses[Math.floor(rand() * warehouses.length)];
      
      // Payment date is 60 days after inbound date
      const paymentDate = new Date(randomDate);
      paymentDate.setDate(paymentDate.getDate() + 60);
      
      data.push({
        type: types[Math.floor(rand() * types.length)],
        number: String(startNumber + i),
        skuNumber: String(50000000 + Math.floor(rand() * 2000000)),
        skuName: productNames[Math.floor(rand() * productNames.length)],
        date: formatDateTime(randomDate),
        warehouse: warehouse,
        taxType: taxTypes[Math.floor(rand() * taxTypes.length)],
        quantity: quantity,
        unitPrice: unitPrice.toLocaleString(),
        supplyPrice: supplyPrice.toLocaleString(),
        vatPrice: vatPrice.toLocaleString(),
        totalUnitPrice: (unitPrice * quantity).toLocaleString(),
        totalSupplyPrice: (supplyPrice * quantity).toLocaleString(),
        totalVatPrice: (vatPrice * quantity).toLocaleString(),
        invoiceNumber: String(27500000 + Math.floor(Math.random() * 200000)),
        paymentDate: formatDate(paymentDate)
      });
    }
    
    return { data, firstPageDate: formatDateTime(firstPageDate), firstPageWarehouse, firstPagePaymentDate: formatDate(firstPagePaymentDate) };
  };

  const inboundData = useMemo(() => 
    generateInboundData(committedFilters.startDate, committedFilters.endDate, user?.username || 'elonmusk'),
    [committedFilters.startDate, committedFilters.endDate, user?.username]
  );

  // Shuffle and stamp first 10 items with shared date/warehouse
  const shuffleAndStamp = ({ data, firstPageDate, firstPageWarehouse, firstPagePaymentDate }) => {
    const shuffled = [...data];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    for (let i = 0; i < Math.min(10, shuffled.length); i++) {
      shuffled[i] = { ...shuffled[i], date: firstPageDate, warehouse: firstPageWarehouse, paymentDate: firstPagePaymentDate };
    }
    return shuffled;
  };

  // State to hold displayed data - initialize with inbound data
  const [displayedData, setDisplayedData] = useState(() => shuffleAndStamp(generateInboundData('2026-04-01', '2026-04-30', 'elonmusk')));

  // Sync displayedData when inboundData changes (e.g. after search for non-Apr-2026 months)
  useEffect(() => {
    if (!(committedFilters.startDate >= '2026-04-01' && committedFilters.endDate <= '2026-04-30')) {
      setDisplayedData(shuffleAndStamp(inboundData));
    }
  }, [inboundData]);
  
  // Pagination constants
  const itemsPerPage = 10;
  const totalPages = Math.ceil(displayedData.length / itemsPerPage);
  
  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return displayedData.slice(startIndex, endIndex);
  };
  
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      
      // Update URL with new page number
      const queryParams = new URLSearchParams({
        page: page.toString(),
        totalOrderPrice: summaryData.totalOrderPrice.replace(/,/g, ''),
        totalUnitPrice: summaryData.totalUnitPrice.replace(/,/g, ''),
        totalVatPrice: summaryData.totalVatPrice.replace(/,/g, ''),
        totalCount: summaryData.totalCount.replace(/,/g, ''),
        startDate: filters.startDate,
        endDate: filters.endDate,
        requestSeq: filters.requestSeq || '',
        vendorPaymentInfoSeq: filters.vendorPaymentInfoSeq || ''
      });
      
      navigate(`/scm/receive/detail?${queryParams.toString()}`, { replace: true });
    }
  };

  const originalInboundData = [
    {
      type: '발주',
      number: '115232204',
      skuNumber: '51169506',
      skuName: '파이네스트원 어메이즈핏 액티브 44.45mm 액정보호 TPU 스마트워치 케이스 블랙 1개',
      date: '2025-11-01 00:59:09',
      warehouse: 'INC14',
      taxType: '과세',
      quantity: 2,
      unitPrice: '4,400',
      supplyPrice: '4,000',
      vatPrice: '400',
      totalUnitPrice: '8,800',
      totalSupplyPrice: '8,000',
      totalVatPrice: '800',
      invoiceNumber: '27566130',
      paymentDate: '2025-12-03'
    },
    {
      type: '발주',
      number: '115249974',
      skuNumber: '51145762',
      skuName: '파이네스트원 어메이즈핏 GTS 4 MINI 41.8mm 액정보호 TPU 스마트워치 케이스 블랙 1개',
      date: '2025-11-06 12:02:22',
      warehouse: 'INC14',
      taxType: '과세',
      quantity: 5,
      unitPrice: '4,400',
      supplyPrice: '4,000',
      vatPrice: '400',
      totalUnitPrice: '22,000',
      totalSupplyPrice: '20,000',
      totalVatPrice: '2,000',
      invoiceNumber: '27632616',
      paymentDate: '2026-01-05'
    },
    {
      type: '발주',
      number: '115250111',
      skuNumber: '51169505',
      skuName: '파이네스트원 어메이즈핏 액티브 44.45mm 액정보호 TPU 스마트워치 케이스 실버 1개',
      date: '2025-11-01 00:59:09',
      warehouse: 'INC14',
      taxType: '과세',
      quantity: 7,
      unitPrice: '4,400',
      supplyPrice: '4,000',
      vatPrice: '400',
      totalUnitPrice: '30,800',
      totalSupplyPrice: '28,000',
      totalVatPrice: '2,800',
      invoiceNumber: '27566130',
      paymentDate: '2025-12-03'
    },
    {
      type: '발주',
      number: '115291342',
      skuNumber: '58733589',
      skuName: '퀸스벨 이쁜 수납정리함 라지-케이스4단-입[팬티+양말+내의]',
      date: '2025-11-07 02:45:03',
      warehouse: 'ECH4',
      taxType: '과세',
      quantity: 1,
      unitPrice: '29,300',
      supplyPrice: '26,636',
      vatPrice: '2,664',
      totalUnitPrice: '29,300',
      totalSupplyPrice: '26,636',
      totalVatPrice: '2,664',
      invoiceNumber: '27646277',
      paymentDate: '2026-01-06'
    },
    {
      type: '발주',
      number: '115327771',
      skuNumber: '50962173',
      skuName: '킹스로드 예쁜 코스튬 머리띠  퍼플 머메이드',
      date: '2025-11-06 14:12:38',
      warehouse: 'INC32',
      taxType: '과세',
      quantity: 1,
      unitPrice: '7,700',
      supplyPrice: '7,000',
      vatPrice: '700',
      totalUnitPrice: '7,700',
      totalSupplyPrice: '7,000',
      totalVatPrice: '700',
      invoiceNumber: '27632617',
      paymentDate: '2026-01-02'
    },
    {
      type: '발주',
      number: '115327771',
      skuNumber: '50962198',
      skuName: '킹스로드 예쁜 코스튬 머리띠  크라운 미키',
      date: '2025-11-06 14:12:03',
      warehouse: 'INC32',
      taxType: '과세',
      quantity: 3,
      unitPrice: '5,868',
      supplyPrice: '5,335',
      vatPrice: '533',
      totalUnitPrice: '17,604',
      totalSupplyPrice: '16,005',
      totalVatPrice: '1,599',
      invoiceNumber: '27632617',
      paymentDate: '2026-01-02'
    },
    {
      type: '발주',
      number: '115331005',
      skuNumber: '51117431',
      skuName: '파이네스트원 가민 265/265S 크로스 패턴 스마트워치 스트랩 다크 그레이18mm(265S)',
      date: '2025-11-05 22:11:39',
      warehouse: 'INC32',
      taxType: '과세',
      quantity: 1,
      unitPrice: '3,700',
      supplyPrice: '3,364',
      vatPrice: '336',
      totalUnitPrice: '3,700',
      totalSupplyPrice: '3,364',
      totalVatPrice: '336',
      invoiceNumber: '27618915',
      paymentDate: '2026-01-02'
    },
    {
      type: '발주',
      number: '115331005',
      skuNumber: '51117450',
      skuName: '파이네스트원 가민 265/265S 믹스 컬러 스마트워치 스트랩 오렌지 블랙22mm(265)',
      date: '2025-11-05 22:11:39',
      warehouse: 'INC32',
      taxType: '과세',
      quantity: 4,
      unitPrice: '5,200',
      supplyPrice: '4,727',
      vatPrice: '473',
      totalUnitPrice: '20,800',
      totalSupplyPrice: '18,908',
      totalVatPrice: '1,892',
      invoiceNumber: '27618915',
      paymentDate: '2026-01-02'
    },
    {
      type: '발주',
      number: '115331005',
      skuNumber: '51117456',
      skuName: '파이네스트원 가민 265/265S 믹스 컬러 스마트워치 스트랩 화이트 블랙22mm(265)',
      date: '2025-11-06 14:41:08',
      warehouse: 'INC32',
      taxType: '과세',
      quantity: 1,
      unitPrice: '5,200',
      supplyPrice: '4,727',
      vatPrice: '473',
      totalUnitPrice: '5,200',
      totalSupplyPrice: '4,727',
      totalVatPrice: '473',
      invoiceNumber: '27632617',
      paymentDate: '2026-01-02'
    },
    {
      type: '발주',
      number: '115338013',
      skuNumber: '51114820',
      skuName: '파이네스트원 가민 포러너 165 패턴 실리콘 스마트위치 스트랩 포기 그레이 20mm',
      date: '2025-11-06 14:57:00',
      warehouse: 'INC32',
      taxType: '과세',
      quantity: 3,
      unitPrice: '5,300',
      supplyPrice: '4,818',
      vatPrice: '482',
      totalUnitPrice: '15,900',
      totalSupplyPrice: '14,454',
      totalVatPrice: '1,446',
      invoiceNumber: '27632617',
      paymentDate: '2026-01-02'
    }
  ];

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [name]: value
      };
      
      // Auto-update date range when year or month changes
      if (name === 'year' || name === 'month') {
        const year = name === 'year' ? value : prev.year;
        const month = name === 'month' ? value : prev.month;
        
        // Get first day of month
        const startDate = `${year}-${month.padStart(2, '0')}-01`;
        
        // Get last day of month
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month.padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
        
        newFilters.startDate = startDate;
        newFilters.endDate = endDate;
      }
      
      return newFilters;
    });
  };

  const handleSearch = () => {
    console.log('Searching with filters:', filters);
    setCommittedFilters({
      year: filters.year,
      month: filters.month,
      startDate: filters.startDate,
      endDate: filters.endDate
    });
    setSearchPerformed(true);
    setCurrentPage(1);
    
    // Update URL with query parameters
    const queryParams = new URLSearchParams({
      page: '1',
      totalOrderPrice: summaryData.totalOrderPrice.replace(/,/g, ''),
      totalUnitPrice: summaryData.totalUnitPrice.replace(/,/g, ''),
      totalVatPrice: summaryData.totalVatPrice.replace(/,/g, ''),
      totalCount: summaryData.totalCount.replace(/,/g, ''),
      startDate: filters.startDate,
      endDate: filters.endDate,
      requestSeq: filters.requestSeq || '',
      vendorPaymentInfoSeq: filters.vendorPaymentInfoSeq || ''
    });
    
    navigate(`/scm/receive/detail?${queryParams.toString()}`, { replace: true });
  };

  const handleDownload = () => {
    console.log('Downloading Excel...');
  };

  const handleResetFilter = () => {
    setFilters({
      year: '2026',
      month: '4',
      startDate: '2026-04-01',
      endDate: '2026-04-30',
      requestSeq: '',
      vendorPaymentInfoSeq: ''
    });
  };

  const handleMenuClick = (menu) => {
    if (menu === 'logistics') {
      setIsLogisticsExpanded(!isLogisticsExpanded);
    } else {
      setIsLogisticsExpanded(false);
    }
  };

  const years = [];
  for (let i = new Date().getFullYear() + 1; i >= 2015; i--) {
    years.push(i);
  }

  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="inbound-details">
      <Header activeMenu={activeMenu} onMenuClick={handleMenuClick} />
      {activeMenu === 'logistics' && isLogisticsExpanded && <SubNav />}
      <div className="contentsArea">
        <div className="titleArea">
          <h3>입고상세내역</h3>
        </div>
        
        <div className="scmContentsArea">
          <div className="noticeType2">
            <div>
              <h4 className="noticeType2Title">알려드립니다.</h4>
              <ul className="noticeType2Body">
                <li>• 입고/반출일자를 기준으로 D+60에 정산지급 됩니다.</li>
              </ul>
            </div>
            <button className="close-btn" onClick={() => document.querySelector('.noticeType2').style.display = 'none'}>✕</button>
          </div>

          <div className="filterArea">
            <div className="tabButtonArea" style={{marginBottom: '15px', paddingBottom: '10px', borderBottom: '1px solid #e8e8e8'}}>
              <button type="button" className="btn default-button" onClick={handleDownload}>
                다운로드<span className="download-icon"></span>
              </button>
              <span className="divider-large"></span>
              <button type="button" className="btn default-button" onClick={handleResetFilter}>
                <span className="refresh-icon"></span> 필터초기화
              </button>
              <button type="button" className="btn default-button filterToggle">
                필터<span className="up-icon"></span>
              </button>
            </div>

            <form id="searchForm" onSubmit={(e) => { e.preventDefault(); handleSearch(); }}>
              <div className="filterBody">
                <div className="form-row-group">
                  <dl className="inline-row">
                    <dt>월별검색</dt>
                    <dd>
                      <div className="form-row align-items-center">
                        <div className="col-sm-3">
                          <select 
                            id="year" 
                            className="form-control custom-select"
                            name="year"
                            value={filters.year}
                            onChange={handleFilterChange}
                          >
                            {years.map(year => (
                              <option key={year} value={year}>{year}년</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-2">
                          <select 
                            id="month" 
                            className="form-control custom-select"
                            name="month"
                            value={filters.month}
                            onChange={handleFilterChange}
                          >
                            {months.map(month => (
                              <option key={month} value={month}>{month}월</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </dd>
                  </dl>
                  
                  <dl className="inline-row">
                    <dt>기간검색</dt>
                    <dd className="dateGroup">
                      <DatePicker
                        selected={filters.startDate ? new Date(filters.startDate) : null}
                        onChange={(date) => {
                          if (!showStartMonthPicker) {
                            // When in day picker mode, select the date normally
                            const formattedDate = date ? date.toISOString().split('T')[0] : '';
                            handleFilterChange({ target: { name: 'startDate', value: formattedDate } });
                          }
                        }}
                        onMonthChange={(date) => {
                          // When month is selected, switch to day view without closing popup
                          setShowStartMonthPicker(false);
                        }}
                        shouldCloseOnSelect={!showStartMonthPicker}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="시작일"
                        locale={ko}
                        showMonthYearPicker={showStartMonthPicker}
                        renderCustomHeader={({
                          date,
                          changeYear,
                          changeMonth,
                          decreaseMonth,
                          increaseMonth,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => (
                          <div className="react-datepicker__header-custom">
                            {!showStartMonthPicker && (
                              <>
                                <button
                                  type="button"
                                  onClick={decreaseMonth}
                                  disabled={prevMonthButtonDisabled}
                                  className="react-datepicker__navigation react-datepicker__navigation--previous"
                                >
                                  <span className="react-datepicker__navigation-icon">‹</span>
                                </button>
                                <h2 
                                  className="react-datepicker__current-month-custom"
                                  onClick={() => setShowStartMonthPicker(true)}
                                >
                                  {date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
                                </h2>
                                <button
                                  type="button"
                                  onClick={increaseMonth}
                                  disabled={nextMonthButtonDisabled}
                                  className="react-datepicker__navigation react-datepicker__navigation--next"
                                >
                                  <span className="react-datepicker__navigation-icon">›</span>
                                </button>
                              </>
                            )}
                            {showStartMonthPicker && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => changeYear(date.getFullYear() - 1)}
                                  className="react-datepicker__navigation react-datepicker__navigation--previous"
                                >
                                  <span className="react-datepicker__navigation-icon">‹</span>
                                </button>
                                <h2 className="react-datepicker__current-month-custom">
                                  {date.getFullYear()}
                                </h2>
                                <button
                                  type="button"
                                  onClick={() => changeYear(date.getFullYear() + 1)}
                                  className="react-datepicker__navigation react-datepicker__navigation--next"
                                >
                                  <span className="react-datepicker__navigation-icon">›</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        customInput={<CustomDateInput />}
                      />
                      <span className="swungDash">~</span>
                      <DatePicker
                        selected={filters.endDate ? new Date(filters.endDate) : null}
                        onChange={(date) => {
                          if (!showEndMonthPicker) {
                            // When in day picker mode, select the date normally
                            const formattedDate = date ? date.toISOString().split('T')[0] : '';
                            handleFilterChange({ target: { name: 'endDate', value: formattedDate } });
                          }
                        }}
                        onMonthChange={(date) => {
                          // When month is selected, switch to day view without closing popup
                          setShowEndMonthPicker(false);
                        }}
                        shouldCloseOnSelect={!showEndMonthPicker}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="종료일"
                        locale={ko}
                        showMonthYearPicker={showEndMonthPicker}
                        renderCustomHeader={({
                          date,
                          changeYear,
                          changeMonth,
                          decreaseMonth,
                          increaseMonth,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => (
                          <div className="react-datepicker__header-custom">
                            {!showEndMonthPicker && (
                              <>
                                <button
                                  type="button"
                                  onClick={decreaseMonth}
                                  disabled={prevMonthButtonDisabled}
                                  className="react-datepicker__navigation react-datepicker__navigation--previous"
                                >
                                  <span className="react-datepicker__navigation-icon">‹</span>
                                </button>
                                <h2 
                                  className="react-datepicker__current-month-custom"
                                  onClick={() => setShowEndMonthPicker(true)}
                                >
                                  {date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
                                </h2>
                                <button
                                  type="button"
                                  onClick={increaseMonth}
                                  disabled={nextMonthButtonDisabled}
                                  className="react-datepicker__navigation react-datepicker__navigation--next"
                                >
                                  <span className="react-datepicker__navigation-icon">›</span>
                                </button>
                              </>
                            )}
                            {showEndMonthPicker && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => changeYear(date.getFullYear() - 1)}
                                  className="react-datepicker__navigation react-datepicker__navigation--previous"
                                >
                                  <span className="react-datepicker__navigation-icon">‹</span>
                                </button>
                                <h2 className="react-datepicker__current-month-custom">
                                  {date.getFullYear()}
                                </h2>
                                <button
                                  type="button"
                                  onClick={() => changeYear(date.getFullYear() + 1)}
                                  className="react-datepicker__navigation react-datepicker__navigation--next"
                                >
                                  <span className="react-datepicker__navigation-icon">›</span>
                                </button>
                              </>
                            )}
                          </div>
                        )}
                        customInput={<CustomDateInput />}
                      />
                      <div className="btn-group">
                        <button type="button" className="btn default-button">어제</button>
                        <button type="button" className="btn default-button">오늘</button>
                        <button type="button" className="btn default-button">최근1주</button>
                        <button type="button" className="btn default-button">최근 1달</button>
                      </div>
                    </dd>
                  </dl>
                </div>
                
                <div className="form-row-group">
                  <dl className="inline-row">
                    <dt>발주/ 반출번호</dt>
                    <dd className="form-group">
                      <input 
                        type="text" 
                        className="form-control" 
                        name="requestSeq"
                        value={filters.requestSeq}
                        onChange={handleFilterChange}
                        placeholder="발주/ 반출번호를 입력해주세요" 
                      />
                    </dd>
                  </dl>
                  
                  <dl className="inline-row">
                    <dt>계산서번호</dt>
                    <dd className="form-group">
                      <input 
                        type="text" 
                        className="form-control" 
                        name="vendorPaymentInfoSeq"
                        value={filters.vendorPaymentInfoSeq}
                        onChange={handleFilterChange}
                        placeholder="계산서번호를 입력해주세요"
                      />
                    </dd>
                  </dl>
                </div>
              </div>
              
              <div className="tabButtonArea">
                <button type="submit" className="btn btn-primary" style={{marginTop: '10px'}}>검색</button>
              </div>
            </form>

            {searchPerformed && (
              <>
                <table className="scmTable basic marginT" style={{marginTop: '20px'}}>
                  <tbody>
                    <tr>
                      <th className="none center">총 단가 합계</th>
                      <th className="none center">총 공급가 합계</th>
                      <th className="none center">총 세액 합계</th>
                      <th className="none center">검색 건수</th>
                    </tr>
                    <tr>
                      <td className="center">{summaryData.totalUnitPrice}</td>
                      <td className="center">{summaryData.totalOrderPrice}</td>
                      <td className="center">{summaryData.totalVatPrice}</td>
                      <td className="center">{summaryData.totalCount}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="filterBody">
                  <p align="center">
                    * 현재 검색된 목록의 <label>총 합계</label> 금액 내역입니다.
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="scmTableArea">
            <table className="scmTable basic marginT">
              <tbody>
                <tr>
                  <th className="none">구분</th>
                  <th className="none">번호</th>
                  <th className="none">SKU 번호</th>
                  <th className="none">SKU 명</th>
                  <th className="none">입고/반출일자</th>
                  <th className="none">물류센터</th>
                  <th className="none">세금타입</th>
                  <th className="none">수량</th>
                  <th className="none">단가</th>
                  <th className="none">공급가액</th>
                  <th className="none">세액</th>
                  <th className="none">총 단가</th>
                  <th className="none">총 공급가액</th>
                  <th className="none">총 세액</th>
                  <th className="none">계산서번호</th>
                  <th className="none">지급일</th>
                </tr>
                
                {(committedFilters.startDate >= '2026-04-01' && committedFilters.endDate <= '2026-04-30') ? (
                  <tr>
                    <td colSpan="16" className="center" style={{padding: '20px', color: '#666'}}>
                      해당하는 검색조건에 대한 결과가 없습니다.
                    </td>
                  </tr>
                ) : (
                  getCurrentPageItems().map((item, index) => (
                  <tr key={index}>
                    <td className="center">{item.type}</td>
                    <td className="center">
                      <a href="#" className="link-number">{item.number}</a>
                    </td>
                    <td>{item.skuNumber}</td>
                    <td className="center">{item.skuName}</td>
                    <td className="center">{item.date}</td>
                    <td className="center">{item.warehouse}</td>
                    <td className="center">{item.taxType}</td>
                    <td className="right">{item.quantity}</td>
                    <td className="right">{item.unitPrice}</td>
                    <td className="right">{item.supplyPrice}</td>
                    <td className="right">{item.vatPrice}</td>
                    <td className="right">{item.totalUnitPrice}</td>
                    <td className="right">{item.totalSupplyPrice}</td>
                    <td className="right">{item.totalVatPrice}</td>
                    <td className="center">
                      <a href="#" className="link-number">{item.invoiceNumber}</a>
                    </td>
                    <td className="center">{item.paymentDate}</td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>

            {!(committedFilters.startDate >= '2026-04-01' && committedFilters.endDate <= '2026-04-30') && (
            <div className="paginationArea">
              <ul className="scmPaginate">
                <li className="prev">
                  <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}>이전</a>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i + 1} className={currentPage === i + 1 ? 'active' : ''}>
                    <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(i + 1); }}>{i + 1}</a>
                  </li>
                ))}
                <li className="next">
                  <a href="#" onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}>다음</a>
                </li>
              </ul>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-left">
            <p className="phone">서플라이어 콜센터 : 1600-9879 (월-금 9:00-19:00)</p>
            <p className="copyright">© Copyright 2025 Coupang.com, or its affiliates. All rights reserved.</p>
          </div>
          <div className="footer-right">
            <a href="#">Speak Up 제보 / Ethics 헬프라인</a>
            <a href="#">공정거래 업무자료</a>
            <a href="#">이용약관</a>
            <a href="#">개인정보 처리방침</a>
            <a href="#">고객지원</a>
            <a href="#">은퇴로 문의</a>
            <a href="#">낮은 윤리약</a>
            <a href="#">도움말</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default InboundDetails;
